import { AfterViewInit, Component, effect, inject, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { isPlatformBrowser } from '@angular/common';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { environment } from '../../../environments/environment';
import { NoWalletComponent } from '../../modals/no-wallet/no-wallet.component';
import { AnalyticsService } from '../../services/analytics.service';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { EventService } from '../../services/event.service';
import { PixelService } from '../../services/pixel.service';
import { WINDOW } from '../../services/window';
import { NO_WALLET_MODAL_DELAY_MS, ROUND_END_REFRESH_DELAY_MS, WIDGET_REFRESH_RATE_MS } from '../../shared/constants/app-config.const';
import { CHAINS, getUserDevice, SwapStatus } from '../../shared/constants/app-enums';
import { getChainData } from '../../shared/constants/wallet-connect.const';
import { ICounter, RaisedInfo } from '../../shared/interfaces/common.interface';
import { IBalnceDataEVM, IBalnceDataSOL, ITokenomics } from '../../shared/interfaces/wallet.interface';

@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrl: './widget.component.scss',
  standalone: false
})
export class WidgetV2 implements AfterViewInit, OnDestroy {
  private ngZone = inject(NgZone);

  isBsNetwork: boolean = false;
  tokenSold = 0;
  maxAmount = 0;
  maxToken = 0;
  minToken = 0;
  usdRaised = 0;
  currentStep = 0;
  userStaked: number = 0;
  userClaimable: number = 0;
  userEthClaimable: number = 0;
  refreshId!: ReturnType<typeof setInterval>;
  refreshRate: number = WIDGET_REFRESH_RATE_MS;
  dataLoaded: boolean = false;
  salePrice: number = 0.0337;
  liveCounter: ICounter = { _days: 0, _hours: 0, _minutes: 0, _seconds: 0, _diff: 0 };
  preliveCounter: ICounter = { _days: 0, _hours: 0, _minutes: 0, _seconds: 0, _diff: 0 };
  swapMode: string = 'native';
  isNative: boolean = true;
  isWert: boolean = false;
  referUrl: string = '';
  SwapStatus = SwapStatus;
  timeInterval!: ReturnType<typeof setInterval>;

  walletAddress!: string;
  currentStoreData!: ITokenomics;
  status: SwapStatus = SwapStatus.not_started;
  raisedPercent!: string;
  modeName!: string;
  tokenName = environment.tokenName;
  raisedInfo: RaisedInfo = {
    raisedPercent: "0%",
    usdRaised: 0,
    userClaimable: 0,
    userEthClaimable: 0,
    userStaked: 0,
    maxAmount: 0,
  };
  isPresalestarted: boolean = true;


  private readonly _platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly eventService = inject(EventService);
  private readonly modalService = inject(BsModalService);
  readonly dataService = inject(PresaleDataService);
  private modalRef = inject(BsModalRef);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly pixelService = inject(PixelService);

  constructor() {
    this.eventService.getEvent('ROUND-END').pipe(takeUntilDestroyed()).subscribe(() => {
      this.eventService.resetCounter();
      this.dataService.localStep++;
      this.dataService.getPresalesData();
      // Brief delay lets the new round data load before restarting the countdown timer
      setTimeout(() => this.setTimer(), ROUND_END_REFRESH_DELAY_MS);
    });

    effect(() => {
      const data = this.dataService.widgetBalanceData();
      this.getBalanceData();
      this.setTimer();
    });

    effect(() => {
      this.liveCounter = this.eventService.daysCounter();
      this.preliveCounter = this.eventService.daysCounter();
    });

  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.ngZone.runOutsideAngular(() => {
        this.timeInterval = setInterval(() => {
          this.ngZone.run(() => this.setTimer());
        }, this.refreshRate);
      });
    }
  }

  getBalanceData() {
    const balanceData: IBalnceDataEVM = this.dataService.balanceDataEVM;
    const solBalanceData: IBalnceDataSOL = this.dataService.balanceDataSOl;
    const isEVM = this.dataService.activeChain === CHAINS.EVM;
    this.currentStep = balanceData.currentStep;
    this.tokenSold = isEVM ? (balanceData.totalTokensSold || 0) : (solBalanceData.totalTokensSoldSOL || 0);
    this.currentStoreData = this.dataService.tokenomics[this.currentStep];
    this.maxAmount = (this.currentStoreData?.maxAmount || 0);
    this.maxToken = this.currentStoreData?.maxToken || 0;
    this.minToken = this.currentStoreData?.minToken || 0;
    this.usdRaised = (balanceData.totalUsdRaised ?? 0) + (solBalanceData.usdRaised ?? 0);
    this.userStaked = this.dataService.userStaked;
    this.userClaimable = isEVM ? balanceData.totalClaimable : solBalanceData.userClaimableSOL;
    this.userEthClaimable = balanceData.ethClaimable ?? 0;
    this.referUrl = this.analyticsService.referUrl;
    // this.status = this.walletConnectService.swapStatus;
    this.walletAddress = isEVM ? this.dataService.walletAddress : this.dataService.walletAddressSOL;
    this.modeName = getChainData(this.dataService.chainId).purchaseToken;
    this.salePrice = this.currentStoreData?.tokenUSDT;
    this.raisedPercent = this.getRaisedPercent();
    this.dataLoaded = true;


    this.raisedInfo = {
      raisedPercent: this.raisedPercent,
      usdRaised: this.usdRaised,
      userClaimable: this.userClaimable,
      userEthClaimable: this.userEthClaimable,
      userStaked: this.userStaked,
      maxAmount: this.maxAmount,
    };

  }

  connectModal(placement: 'Widget-Crypto' | 'Widget-Card'): void {
    this.analyticsService.vwoData('walletConnectionPlacement', { placement });
    this.dataService.connectWallet(placement === 'Widget-Card' ? 'ETH' : '');
  }


  getRaisedPercent() {
    let percent = (this.usdRaised / this.maxAmount) * 100;
    return (percent >= 100 ? 100 : percent <= 80 ? 80 : percent) + '%';
  }

  ngOnDestroy(): void { }

  setTimer() {
    if (this.dataLoaded && this.currentStoreData?.endDate) {
      if (this.currentStoreData.endDate < Date.parse(new Date().toString()) && this.currentStep < this.dataService.tokenomics.length - 1) {
        this.eventService.setCounter(this.dataService.tokenomics[this.currentStep + 1].endDate);
      } else {
        this.eventService.setCounter(this.currentStoreData.endDate);
      }
      clearInterval(this.timeInterval);
    }
  }

  connectWallet(mode: string): void {
    this.dataService.connectWallet();
    this.modalRef.hide();
  }

  openBestWallet() {
    this.modalRef.hide();
    this.analyticsService.vwoData('dontHaveWallet', { noWalletOptionClickedDevice: getUserDevice(), noWalletOptionClickedPlacement: 'Widget' });
    // Delay allows the current modal to fully close before opening NoWallet
    setTimeout(() => {
      this.modalRef = this.modalService.show(NoWalletComponent, {
        class: 'modal-lg modal-dialog-centered bw-modal',
        backdrop: 'static',
      });
    }, NO_WALLET_MODAL_DELAY_MS);
  }

  trackPartnerClick(): void {
    this.pixelService.trackPageClick('https://web3paymentsolutions.io', 'web3payments');
  }
}
